<template>
  <div class="tw-relative">
    <Popover v-model:open="isOpen">
      <PopoverTrigger as-child>
        <Button variant="secondary" class="tw-w-full tw-justify-start tw-text-left tw-font-normal">
          <CalendarIcon class="tw-mr-2 tw-h-4 tw-w-4" />
          <span v-if="!dateRange.from">
            {{ placeholder }}
          </span>
          <span v-else-if="!dateRange.to">
            {{ format(dateRange.from, 'LLL dd, y') }}
          </span>
          <span v-else>
            {{ format(dateRange.from, 'LLL dd, y') }} -
            {{ format(dateRange.to, 'LLL dd, y') }}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent class="tw-w-auto tw-p-0" align="start">
        <div class="tw-flex">
          <!-- Presets Sidebar -->
          <div class="tw-flex tw-flex-col tw-border-r tw-border-gray-200">
            <div class="tw-p-3 tw-border-b tw-border-gray-200">
              <h4 class="tw-text-sm tw-font-medium tw-text-gray-700">
                {{ t('common.datepicker.presets') }}
              </h4>
            </div>
            <div class="tw-flex tw-flex-col tw-space-y-1 tw-p-3">
              <button
                v-for="preset in presetRanges"
                :key="preset.label"
                @click="handlePresetClick(preset.range)"
                class="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-rounded-md tw-hover:bg-gray-100 tw-transition-colors tw-whitespace-nowrap"
              >
                {{ preset.label }}
              </button>
            </div>
            <div v-if="showClearButton" class="tw-border-t tw-border-gray-200 tw-p-3">
              <Button variant="outline" size="sm" @click="handleClear" class="tw-w-full">
                {{ t('common.actions.clear') }}
              </Button>
            </div>
          </div>

          <!-- Calendar Section -->
          <div class="tw-p-3">
            <RangeCalendar
              v-model="internalDateRange"
              @update:model-value="handleDateRangeChange"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-vue-next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { RangeCalendar } from '@/components/ui/range-calendar';
import { CalendarDate, getLocalTimeZone } from '@internationalized/date';
import {
  endOfMonth,
  endOfYear,
  endOfWeek,
  startOfMonth,
  startOfYear,
  startOfWeek,
  subMonths,
  subYears,
  subWeeks,
  subDays,
} from 'date-fns';

const { t } = useI18n();

interface PresetRange {
  label: string;
  range: [Date, Date];
}

interface Props {
  placeholder?: string;
  modelValue?: {
    from: Date | undefined;
    to: Date | undefined;
  };
  showClearButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Pick a date range',
  modelValue: () => ({ from: undefined, to: undefined }),
  showClearButton: true,
});

const emit = defineEmits<{
  'update:modelValue': [value: { from: Date | undefined; to: Date | undefined }];
}>();

// Track dropdown state
const isOpen = ref(false);

// Internal date range state - this is what the calendar component uses
const internalDateRange = ref<any>({
  start: undefined,
  end: undefined,
});

// Computed date range for the component
const dateRange = computed(() => props.modelValue);

// Preset ranges
const presetRanges = computed((): PresetRange[] => [
  {
    label: t('common.datepicker.last_week'),
    range: [
      startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
    ] as [Date, Date],
  },
  {
    label: t('common.datepicker.last_30_days'),
    range: [subDays(new Date(), 30), new Date()] as [Date, Date],
  },
  {
    label: t('common.datepicker.this_month'),
    range: [startOfMonth(new Date()), new Date()] as [Date, Date],
  },
  {
    label: t('common.datepicker.last_month'),
    range: [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] as [
      Date,
      Date
    ],
  },
  {
    label: t('common.datepicker.this_year'),
    range: [startOfYear(new Date()), new Date()] as [Date, Date],
  },
  {
    label: t('common.datepicker.last_year'),
    range: [startOfYear(subYears(new Date(), 1)), endOfYear(subYears(new Date(), 1))] as [
      Date,
      Date
    ],
  },
]);

// Handle preset click
const handlePresetClick = (range: [Date, Date]) => {
  const [startDate, endDate] = range;

  const startCalendar = new CalendarDate(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    startDate.getDate()
  );

  const endCalendar = new CalendarDate(
    endDate.getFullYear(),
    endDate.getMonth() + 1,
    endDate.getDate()
  );

  internalDateRange.value = {
    start: startCalendar,
    end: endCalendar,
  };

  // Emit the date range
  emit('update:modelValue', {
    from: startDate,
    to: endDate,
  });

  // Close the dropdown
  isOpen.value = false;
};

// Handle clear
const handleClear = () => {
  internalDateRange.value = {
    start: undefined,
    end: undefined,
  };

  emit('update:modelValue', {
    from: undefined,
    to: undefined,
  });

  isOpen.value = false;
};

// Handle date range change from calendar
const handleDateRangeChange = (newRange: any) => {
  if (!newRange || (!newRange.start && !newRange.end)) {
    emit('update:modelValue', {
      from: undefined,
      to: undefined,
    });
    return;
  }

  if (newRange.start && newRange.end) {
    // Convert CalendarDate to Date
    const startDate = newRange.start.toDate(getLocalTimeZone());
    const endDate = newRange.end.toDate(getLocalTimeZone());

    emit('update:modelValue', {
      from: startDate,
      to: endDate,
    });

    // Close the dropdown when both dates are selected
    isOpen.value = false;
  }
};

// Watch for external model value changes
watch(
  () => props.modelValue,
  newValue => {
    if (newValue?.from && newValue?.to) {
      const startCalendar = new CalendarDate(
        newValue.from.getFullYear(),
        newValue.from.getMonth() + 1,
        newValue.from.getDate()
      );

      const endCalendar = new CalendarDate(
        newValue.to.getFullYear(),
        newValue.to.getMonth() + 1,
        newValue.to.getDate()
      );

      internalDateRange.value = {
        start: startCalendar,
        end: endCalendar,
      };
    } else {
      internalDateRange.value = {
        start: undefined,
        end: undefined,
      };
    }
  },
  { immediate: true }
);
</script>
